import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  Optional,
  Renderer2,
  Self,
  SimpleChanges
} from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
  selector: '[appRestrictNumberDecimals]'
})
export class RestrictNumberDecimalsDirective implements OnChanges {

  private static readonly DECIMAL_MIN = '0.01';
  private static readonly DECIMAL_STEP = '0.01';
  private static readonly INTEGER_MIN = '1';
  private static readonly INTEGER_STEP = '1';

  @Input('appRestrictNumberDecimals')
  public enabled = false;

  constructor(
    private readonly host: ElementRef<HTMLInputElement>,
    private readonly renderer: Renderer2,
    @Optional() @Self() private readonly ngControl: NgControl
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['enabled']) {
      this.applyNativeNumberHints();
    }
  }

  @HostListener('keydown', ['$event'])
  public onKeyDown(event: KeyboardEvent): void {
    if (!this.enabled) {
      return;
    }
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    const navigationKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End'
    ];
    if (navigationKeys.includes(event.key)) {
      return;
    }
    if (event.key.length === 1 && event.key >= '0' && event.key <= '9') {
      return;
    }
    event.preventDefault();
  }

  @HostListener('paste', ['$event'])
  public onPaste(event: ClipboardEvent): void {
    if (!this.enabled) {
      return;
    }
    event.preventDefault();
    const text = event.clipboardData?.getData('text/plain') ?? '';
    const pastedDigits = text.replace(/\D/g, '');
    const input = this.host.nativeElement;
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    const current = String(input.value ?? '');
    const beforeDigits = current.slice(0, start).replace(/\D/g, '');
    const afterDigits = current.slice(end).replace(/\D/g, '');
    this.commitIntegerDigits(beforeDigits + pastedDigits + afterDigits);
  }

  @HostListener('input')
  public onInput(): void {
    if (!this.enabled) {
      return;
    }
    const raw = String(this.host.nativeElement.value ?? '');
    const digitsOnly = raw.replace(/\D/g, '');
    if (raw === digitsOnly) {
      return;
    }
    this.commitIntegerDigits(digitsOnly);
  }

  private applyNativeNumberHints(): void {
    const el = this.host.nativeElement;
    if (this.enabled) {
      this.renderer.setAttribute(el, 'min', RestrictNumberDecimalsDirective.INTEGER_MIN);
      this.renderer.setAttribute(el, 'step', RestrictNumberDecimalsDirective.INTEGER_STEP);
    } else {
      this.renderer.setAttribute(el, 'min', RestrictNumberDecimalsDirective.DECIMAL_MIN);
      this.renderer.setAttribute(el, 'step', RestrictNumberDecimalsDirective.DECIMAL_STEP);
    }
  }

  private commitIntegerDigits(digits: string): void {
    const control = this.ngControl?.control;
    if (control) {
      control.setValue(digits === '' ? '' : Number(digits));
    } else {
      this.host.nativeElement.value = digits;
    }
  }
}
